/**
 * NAVIGATION TEMPORELLE DE LA VEILLE — la règle, sans le DOM.
 *
 * Avant, la page empilait toutes les périodes les unes sous les autres et le
 * PASSÉ n'avait pas de case : une soirée de mardi se retrouvait, un samedi,
 * sous le titre « Plus tard ». On ne lisait pas une veille, on triait à la
 * main ce qui était encore devant soi.
 *
 * Ici : une période à la fois, choisie, comptée. Le classement lui-même reste
 * au domaine (`bucketOf`, nuit commerciale) — ce module ne fait que ranger et
 * ordonner ce que le serveur a déjà daté.
 */

export type Periode =
  | 'CE_SOIR' | 'CETTE_SEMAINE' | 'SEMAINE_PROCHAINE' | 'PLUS_TARD'
  | 'SANS_DATE_CONFIRMEE' | 'PASSE'

export type PastRange = 'CETTE_SEMAINE' | 'SEMAINE_DERNIERE' | 'TRENTE_JOURS'

export type Tri = 'DATE' | 'IMPACT' | 'DISTANCE' | 'CONFIANCE'

export const PERIODES: { value: Periode; label: string }[] = [
  { value: 'CE_SOIR', label: 'Ce soir' },
  { value: 'CETTE_SEMAINE', label: 'Cette semaine' },
  { value: 'SEMAINE_PROCHAINE', label: 'Semaine prochaine' },
  { value: 'PLUS_TARD', label: 'Plus tard' },
  { value: 'SANS_DATE_CONFIRMEE', label: 'Date à confirmer' },
  { value: 'PASSE', label: 'Passés' },
]

export const PAST_RANGES: { value: PastRange; label: string }[] = [
  { value: 'CETTE_SEMAINE', label: 'Cette semaine' },
  { value: 'SEMAINE_DERNIERE', label: 'Semaine dernière' },
  { value: 'TRENTE_JOURS', label: '30 jours' },
]

export const TRIS: { value: Tri; label: string }[] = [
  { value: 'DATE', label: 'Date' },
  { value: 'IMPACT', label: 'Impact' },
  { value: 'DISTANCE', label: 'Distance' },
  { value: 'CONFIANCE', label: 'Confiance' },
]

/** Ce dont le tri a besoin — commun aux signaux et aux soirées concurrentes. */
export interface Triable {
  at: string | null
  impact: 'HIGH' | 'MEDIUM' | 'LOW'
  confidence: 'HIGH' | 'MEDIUM' | 'LOW'
  distanceKm: number | null
}

const RANG: Record<'HIGH' | 'MEDIUM' | 'LOW', number> = { HIGH: 0, MEDIUM: 1, LOW: 2 }

/** Le tri PRÉSERVE le type d'entrée : il n'aplatit jamais une carte en
 *  `Triable`, sinon l'écran perdrait tout ce qu'il doit afficher. */

/**
 * PREMIÈRE PÉRIODE QUI A QUELQUE CHOSE À MONTRER.
 *
 * Ouvrir sur « Ce soir » un mardi sans concurrence afficherait un écran vide
 * alors que la semaine est pleine. On descend donc dans l'ordre du produit
 * jusqu'à trouver de la matière ; s'il n'y a rien devant, on regarde derrière.
 */
export function defaultPeriode(counts: Partial<Record<Periode, number>>): Periode {
  for (const p of ['CE_SOIR', 'CETTE_SEMAINE', 'SEMAINE_PROCHAINE', 'PLUS_TARD', 'SANS_DATE_CONFIRMEE'] as const) {
    if ((counts[p] ?? 0) > 0) return p
  }
  return (counts.PASSE ?? 0) > 0 ? 'PASSE' : 'CE_SOIR'
}

/**
 * ORDRE À L'INTÉRIEUR D'UNE PÉRIODE.
 *
 * `DATE` est le défaut : dans une période, ce qui arrive en premier se lit en
 * premier — et dans le passé, ce qui vient de se produire. Les autres tris
 * gardent la date comme départage, sinon deux éléments de même impact
 * s'échangeraient de place à chaque rendu.
 */
export function trier<T extends Triable>(items: T[], tri: Tri, passe = false): T[] {
  const parDate = (a: T, b: T) => {
    if (a.at === null && b.at === null) return 0
    if (a.at === null) return 1
    if (b.at === null) return -1
    return passe ? b.at.localeCompare(a.at) : a.at.localeCompare(b.at)
  }
  const copie = [...items]
  if (tri === 'DATE') return copie.sort(parDate)
  if (tri === 'IMPACT') return copie.sort((a, b) => RANG[a.impact] - RANG[b.impact] || parDate(a, b))
  if (tri === 'CONFIANCE') return copie.sort((a, b) => RANG[a.confidence] - RANG[b.confidence] || parDate(a, b))
  // Distance : les plus proches d'abord ; distance inconnue en dernier, jamais
  // traitée comme un zéro qui la ferait passer devant tout le monde.
  return copie.sort((a, b) => {
    const da = a.distanceKm ?? Number.POSITIVE_INFINITY
    const db = b.distanceKm ?? Number.POSITIVE_INFINITY
    return da - db || parDate(a, b)
  })
}
